/**
 * Наряд-допуск (КЧ-05): анализ воздуха рабочей зоны и журнал наряда. Компоненты и допустимые значения — из настроек
 * нарядов компании; превышение в ходе работ останавливает работы на сервере.
 */
import { PERMIT_EVENT_KINDS, type PermitDetail, type PermitGasView } from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, type Tone, toast } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';

const STATE: Record<PermitGasView['state'], { label: string; tone: Tone }> = {
  not_required: { label: 'Не требуется', tone: 'neutral' },
  none: { label: 'Анализов нет', tone: 'warning' },
  ok: { label: 'В норме', tone: 'success' },
  bad: { label: 'Превышение', tone: 'danger' },
  due: { label: 'Пора повторить анализ', tone: 'danger' },
};

const num = (n: number) => String(n).replace('.', ',');

export function PermitGasPanel({ d, onDone }: { d: PermitDetail; onDone: (d: PermitDetail) => void }) {
  const g = d.gas;
  const [values, setValues] = useState<Record<number, string>>({});
  const [instrument, setInstrument] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const rows = g.norms
        .map((n, i) => ({ n, v: (values[i] ?? '').trim().replace(',', '.') }))
        .filter((x) => x.v !== '')
        .map((x) => ({ component: x.n.component, unit: x.n.unit, result: Number(x.v) }));
      if (!rows.length) throw new Error('Внесите результат хотя бы по одному компоненту');
      if (rows.some((r) => !Number.isFinite(r.result))) throw new Error('Результат — число, например 0,5');
      return api<PermitDetail>(`/quality/permits/${d.id}/gas-tests`, {
        body: { rows, instrument: instrument.trim() || null },
      });
    },
    onSuccess: (r) => {
      onDone(r);
      setValues({});
      toast(
        r.status === 'suspended' && d.status === 'active'
          ? 'Превышение: работы остановлены'
          : r.gas.state === 'bad'
            ? 'Анализ сохранён: есть превышение'
            : 'Анализ сохранён',
        r.gas.state === 'bad' ? 'danger' : 'success',
      );
    },
  });
  if (!g.required && !g.history.length && !d.can.gas) return null;
  return (
    <Panel
      title="Анализ воздуха рабочей зоны"
      meta={
        g.required
          ? `перед началом — не старше ${g.beforeMinutes} мин${g.repeatMinutes ? `, в ходе работ — каждые ${g.repeatMinutes} мин` : ''}`
          : 'для этого вида работ не обязателен'
      }
      actions={<Badge tone={STATE[g.state].tone}>{STATE[g.state].label}</Badge>}
      className="mb-6"
    >
      {g.latest.length ? (
        <ul className="mb-4 flex flex-col gap-2" aria-label="Последние результаты">
          {g.latest.map((r) => (
            <li
              key={r.id}
              className="flex flex-col gap-1 rounded-md border border-line p-3 text-[14px] text-ink md:flex-row md:items-center md:gap-3"
            >
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{r.component}</span>
              <span className={r.ok ? 'font-semibold' : 'font-semibold text-danger'}>
                {num(r.result)} {r.unit}
              </span>
              <span className="text-ink-2">допустимо {r.limit}</span>
              <span className="text-ink-2">{dateTime(r.takenAt)}</span>
              {r.ok ? null : <Badge tone="danger">Превышение</Badge>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-[14px] text-ink-2">Анализов по наряду ещё нет.</p>
      )}
      {g.dueAt && d.status === 'active' ? (
        <p className="mb-4 text-[14px] text-ink-2">Следующий анализ — не позже {dateTime(g.dueAt)}.</p>
      ) : null}
      {d.can.gas ? (
        <form
          className="flex flex-col gap-3 border-t border-line pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <p className="text-[14px] text-ink">
            Новый анализ: для начала и возобновления работ нужны свежие результаты по всем компонентам.
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {g.norms.map((n, i) => (
              <Field
                key={`${n.component}|${n.unit}`}
                label={`${n.component}, ${n.unit}`}
                hint={`допустимо ${n.limit}`}
                htmlFor={`gas-${i}`}
              >
                <Input
                  id={`gas-${i}`}
                  inputMode="decimal"
                  className="h-11 md:h-10"
                  value={values[i] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [i]: e.target.value }))}
                />
              </Field>
            ))}
            <Field label="Газоанализатор" hint="модель, заводской номер" htmlFor="gas-instrument">
              <Input
                id="gas-instrument"
                className="h-11 md:h-10"
                maxLength={200}
                value={instrument}
                onChange={(e) => setInstrument(e.target.value)}
              />
            </Field>
          </div>
          {save.isError ? <ErrorBox error={save.error} /> : null}
          <div>
            <Button type="submit" variant="secondary" size="lg" loading={save.isPending}>
              Добавить анализ
            </Button>
          </div>
        </form>
      ) : null}
      {g.history.length > g.latest.length ? (
        <details className="mt-4">
          <summary className="inline-flex min-h-11 cursor-pointer items-center text-[14px] text-ink underline md:min-h-0">
            Все пробы ({g.history.length})
          </summary>
          <ul className="mt-2 flex flex-col gap-1 text-[13px] text-ink-2">
            {g.history.map((r) => (
              <li key={r.id} className="[overflow-wrap:anywhere]">
                {dateTime(r.takenAt)} · {r.component} {num(r.result)} {r.unit}
                {r.ok ? '' : ' — превышение'} · {r.takenByName}
                {r.instrument ? ` · ${r.instrument}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Panel>
  );
}

export function PermitJournal({ d }: { d: PermitDetail }) {
  if (!d.events.length) return null;
  return (
    <Panel title="Журнал наряда" className="mt-6">
      <ol className="flex flex-col gap-2">
        {d.events.map((e) => (
          <li key={e.id} className="text-[14px] text-ink [overflow-wrap:anywhere]">
            <span className="text-ink-2">{dateTime(e.at)}</span> · {PERMIT_EVENT_KINDS[e.kind]}
            {e.userName ? ` · ${e.userName}` : e.kind === 'suspended' ? ' · портал' : ''}
            {e.text ? <span className="block text-ink-2">{e.text}</span> : null}
          </li>
        ))}
      </ol>
    </Panel>
  );
}
